import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateUpholsteryDesign,
  UpdateUpholsteryDesign,
  UpholsteryDesignOption,
} from '@estimator/shared';
import { apiClient } from './client';

/** Every hook here invalidates this exact key. staleTime is Infinity, so a
 *  mutation that forgets to invalidate leaves the new design invisible until a
 *  hard reload. */
const KEY = ['upholstery-designs'];

/**
 * The catalog behind the "Design Type" dropdown.
 *
 * Returns every row, active or not: a saved configuration holding a retired
 * design must still be able to render that design's real name. Filtering to
 * the active ones is toDesignTypeOptions' job.
 *
 * staleTime: Infinity because the catalog changes only through the two
 * mutations below, both of which invalidate it. Every selector lives inside a
 * modal, and a refetch firing as a modal opens would briefly collapse the
 * option list.
 */
export function useUpholsteryDesigns() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<UpholsteryDesignOption[]>('/upholstery-designs');
      return data;
    },
    staleTime: Infinity,
  });
}

export function useCreateUpholsteryDesign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateUpholsteryDesign) => {
      const { data } = await apiClient.post<UpholsteryDesignOption>('/upholstery-designs', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateUpholsteryDesign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateUpholsteryDesign & { id: string }) => {
      const { data } = await apiClient.patch<UpholsteryDesignOption>(
        `/upholstery-designs/${id}`,
        body,
      );
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
